import { z } from 'zod';

/**
 * The query string on the campaign invite link, and how it is split for the redirect.
 *
 * A module of its own rather than lines inside `routes/nutrition-intake.tsx` so the
 * pass-through can be tested without mounting the route — the same split the rest of this
 * feature uses (`steps.ts`, `outcomeCopy.ts`).
 */

/**
 * ⚠️ **`.passthrough()` is the load-bearing part.** A plain `z.object` STRIPS unknown keys, so
 * `utm_source`, `utm_campaign` and the `token` the SMS path appends would be deleted at
 * validation — before any component could forward them, and with nothing to show for it. If this
 * is ever simplified back to a plain object, the pass-through below silently forwards nothing.
 *
 * `appointmentId` degrades to `''` rather than throwing: a truncated or re-encoded id should show
 * the parent an error, not TanStack's error boundary.
 */
export const inviteSearchSchema = z
  .object({
    appointmentId: z.string().min(1).max(30).catch(''),
    /**
     * The HMAC that authorizes `appointmentId`, minted by `buildLinkUrl` at send time. The
     * resolver rejects without it — an appointment id alone is not a credential, because it is
     * non-secret everywhere else in the company.
     */
    sig: z.string().min(1).max(64).catch(''),
  })
  .passthrough();

export type InviteSearch = z.infer<typeof inviteSearchSchema>;

/**
 * Splits the invite's search params into the id this route consumes and everything to hand on.
 *
 * ⚠️ **`appointmentId` and `sig` are deliberately NOT forwarded.** Both are spent here, and
 * carrying them to the form would put them back in the URL bar — undoing the `replace` that keeps
 * the appointment id out of the history stack, and copying a credential onto a URL that has no
 * use for it. Everything else goes on untouched, so a param added to a future campaign's link
 * needs no change in this file.
 */
export const splitInviteSearch = (
  search: InviteSearch
): { appointmentId: string; sig: string; passThrough: Record<string, unknown> } => {
  const { appointmentId, sig, ...passThrough } = search;
  return { appointmentId, sig, passThrough };
};
